import { ApolloQueryResult, useMutation } from "@apollo/client";
import { IconButton, Tooltip, Box } from "@material-ui/core";
import { LockOpenOutlined, LockOutlined } from "@material-ui/icons";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { BackButton, Button, NotesDropdown, StatusDot, StyledGrid, Link } from "components/simple";
import {
  ClaimUserAssessmentCycle,
  ReleaseUserAssessmentCycle,
  SubmitUserAssessmentCycleForReview
} from "graphql/admin/assessments";
import { useDispatch } from "hooks";
import { UserCycleStatus } from "models/user";
import { color, fontSize, spacingDefaults } from "style/constants";
import { StyledCompletionText, StyledCycleTitle, StyledName } from "./styled";
import ForceReleaseClaimModal from "./ForceReleaseClaimModal";
import SubmitForReviewModal from "./SubmitForReviewModal";
import { UserAssessmentCycle } from "models/userAssessmentCycle";
import CycleSelect from "./CycleSelect";
import { GetAppropriateAssessmentCycleOverviewData } from "./types";

interface Props {
  userAssessmentCycle: UserAssessmentCycle;
  cycleNumbers: number[];
  refetch(): Promise<ApolloQueryResult<GetAppropriateAssessmentCycleOverviewData>>;
  handleCycleChanged(cycleNumber: number): void;
  isCodingClinicianReviewed: boolean;
  refactor: boolean;
}

const StyledIconButton = styled(IconButton)<{ backgroundColor: string }>`
  && {
    background-color: ${props => props.backgroundColor};
    border: 1px solid ${color.DARKERGRAY};
  }
`;

const CycleHeader: React.FC<Props> = ({
  cycleNumbers,
  userAssessmentCycle,
  refetch,
  handleCycleChanged,
  isCodingClinicianReviewed,
  refactor
}: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const selectedCycle = userAssessmentCycle.cycle.cycleNumber;
  const [showForceReleaseClaimModal, setShowForceReleaseClaimModal] = useState(false);
  const [showSubmitForReviewModal, setShowSubmitForReviewModal] = useState(false);

  const [claimCycle] = useMutation(ClaimUserAssessmentCycle);
  const [releaseCycle] = useMutation(ReleaseUserAssessmentCycle);
  const [submitCycleForReview] = useMutation(SubmitUserAssessmentCycleForReview);

  const completionDate = userAssessmentCycle.cycleCompletionDate
    ? new Date(userAssessmentCycle.cycleCompletionDate)
    : null;

  const claimUserName = `${
    userAssessmentCycle.claimUser?.firstName
  } ${userAssessmentCycle.claimUser?.lastName.substring(0, 1)}`;
  const organizationName = userAssessmentCycle.user?.organization?.displayName;
  const organizationUrl = `/organizations/${userAssessmentCycle?.user?.organization?.id}`;
  const userUrl = `/users/${userAssessmentCycle.user?.id}`;

  const handleClaim = async (): Promise<void> => {
    if (userAssessmentCycle.claimUser) {
      if (!userAssessmentCycle.isClaimedByCurrentUser) {
        setShowForceReleaseClaimModal(true);
      } else {
        await releaseCycle({ variables: { id: userAssessmentCycle.id } });
      }
    } else {
      await claimCycle({ variables: { id: userAssessmentCycle.id } });
    }

    await refetch();
  };

  const cycleOptions: Record<string, number>[] = cycleNumbers.map((number, idx) => {
    const cycleOption = `Cycle ${number}`;
    return { [cycleOption]: idx };
  });

  return (
    <StyledGrid
      container
      justifyContent="center"
      width="100%"
      position="sticky"
      top="76px"
      backgroundColor={color.ADMIN_BACKGROUND}
      boxShadow="0px 2px 12px rgba(0, 0, 0, 0.1);"
      padding={`8px ${spacingDefaults.xxlarge} 0`}
      zIndex="50"
      fontSize="20px"
    >
      {refactor && <BackButton state={{ isRefactor: refactor }} />}
      {!refactor && <BackButton />}
      <StyledGrid container maxWidth="1048px" padding={spacingDefaults.normal}>
        <StyledGrid container direction="column" item width="400px">
          <Box fontSize="20px">
            <Link to={userUrl} hoverStyle color="black">
              {userAssessmentCycle.user.firstName}
              {userAssessmentCycle.user.lastName.substring(0, 1)}
            </Link>
          </Box>
          <Box
            fontStyle="italic"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
            width="300px"
            fontSize="15px"
            color={color.BLACK}
          >
            <Link to={organizationUrl} hoverStyle color="black">
              {organizationName}
            </Link>
          </Box>

          <StyledCycleTitle>Assessment Results</StyledCycleTitle>

          <StyledGrid alignItems="center" container item justifyContent="flex-start">
            {completionDate && (
              <StyledCompletionText>
                {`Completed on ${new Date(completionDate).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit"
                })}`}
              </StyledCompletionText>
            )}

            <StyledGrid item marginLeft={spacingDefaults.large}>
              <StatusDot value={userAssessmentCycle.status} />
            </StyledGrid>
          </StyledGrid>
        </StyledGrid>

        <StyledGrid alignItems="center" container item justifyContent="flex-end" xs>
          {cycleNumbers?.length > 1 && (
            <StyledGrid item marginLeft={spacingDefaults.large}>
              <CycleSelect
                name="cycle"
                fullWidth
                callback={(value: string | number) => handleCycleChanged(Number(value))}
                options={cycleOptions}
                defaultText={"View cycle: "}
                selectedValue={String(Number(selectedCycle) - 1)}
              ></CycleSelect>
            </StyledGrid>
          )}

          <NotesDropdown
            notesCallback={() => {
              dispatch({
                type: "open_notes_modal",
                userAssessmentCycleId: userAssessmentCycle.id,
                refetch
              });
            }}
            userAssessmentCycleId={userAssessmentCycle.id}
          />

          {userAssessmentCycle.status !== UserCycleStatus.scoreFinalized &&
            userAssessmentCycle.status !== UserCycleStatus.readyForReview && (
              <StyledGrid marginLeft={spacingDefaults.small}>
                <Button
                  disabled={
                    userAssessmentCycle.status !== UserCycleStatus.scoringInProgress ||
                    !userAssessmentCycle.readyToFinalize ||
                    !userAssessmentCycle.isClaimedByCurrentUser ||
                    !isCodingClinicianReviewed
                  }
                  inverted
                  longText
                  onClick={() => {
                    setShowSubmitForReviewModal(true);
                  }}
                  width="185px"
                >
                  Submit for Review
                </Button>
              </StyledGrid>
            )}

          <StyledGrid marginLeft={spacingDefaults.xsmall}>
            <Button
              disabled={
                userAssessmentCycle.status === UserCycleStatus.scoreFinalized
                  ? false
                  : !userAssessmentCycle.readyToFinalize ||
                    !userAssessmentCycle.isClaimedByCurrentUser ||
                    !isCodingClinicianReviewed
              }
              longText
              onClick={() => {
                const state = {
                  userName: `${
                    userAssessmentCycle.user.firstName
                  } ${userAssessmentCycle.user.lastName.substring(0, 1)}`, //
                  userAssessmentCycleId: userAssessmentCycle.id,
                  status: userAssessmentCycle.status
                };

                history.push(`${history.location.pathname}/finalize`, state);
              }}
              width="185px"
            >
              {userAssessmentCycle.status === UserCycleStatus.scoreFinalized
                ? "View Results"
                : "Finalize Results"}
            </Button>
          </StyledGrid>

          <StyledGrid marginLeft={spacingDefaults.medium}>
            <StyledIconButton
              backgroundColor={
                userAssessmentCycle.claimUser
                  ? userAssessmentCycle.isClaimedByCurrentUser
                    ? color.DARKGREEN
                    : color.DARKRED
                  : "transparent"
              }
              onClick={handleClaim}
            >
              {userAssessmentCycle.claimUser ? (
                <Tooltip
                  title={`Cycle is currently locked by: ${
                    userAssessmentCycle.claimUser.firstName
                  } ${userAssessmentCycle.claimUser.lastName.substring(0, 1)}`} //
                >
                  <LockOutlined style={{ color: color.WHITE }} />
                </Tooltip>
              ) : (
                <LockOpenOutlined style={{ color: color.DARKERGRAY }} />
              )}
            </StyledIconButton>
          </StyledGrid>
        </StyledGrid>
      </StyledGrid>

      <ForceReleaseClaimModal
        show={showForceReleaseClaimModal}
        claimUserName={claimUserName}
        onAccept={async () => {
          await releaseCycle({ variables: { id: userAssessmentCycle.id } });
          await refetch();
          setShowForceReleaseClaimModal(false);
        }}
        onCancel={() => {
          setShowForceReleaseClaimModal(false);
        }}
      />

      <SubmitForReviewModal
        show={showSubmitForReviewModal}
        userAssessmentCycleId={userAssessmentCycle.id}
        notesCount={userAssessmentCycle.internalNotes?.length}
        refetch={refetch}
        onAccept={async () => {
          await submitCycleForReview({ variables: { id: userAssessmentCycle.id } });
          await refetch();
          setShowSubmitForReviewModal(false);
        }}
        onCancel={() => {
          setShowSubmitForReviewModal(false);
        }}
      />
    </StyledGrid>
  );
};

export default CycleHeader;
